import { computed } from '@vue/composition-api';
import useAuth from '@/useAuth';

/**
 * Composable for checking group-based permissions
 * 
 * Usage:
 *   const selectedGroupIds = computed(() => survey.group_ids);
 *   const { canEdit, canView, hasNonAdminGroups } = useGroupPermissions(selectedGroupIds);
 * 
 * @param {Ref<Array<number>>} selectedGroupIds - Reactive reference to array of selected group IDs
 * @returns {Object} Permission checks and helpers
 */
export function useGroupPermissions(selectedGroupIds) {
    const { user } = useAuth();

    // Check if user is tenant admin
    const isTenantAdmin = computed(() => {
        return user.value?.is_tenant_admin === true;
    });

    // Get IDs of groups the user is admin of
    const userAdminGroupIds = computed(() => {
        if (!user.value?.admin_groups) return [];
        return user.value.admin_groups.map(g => g.id);
    });

    // Check if user can edit (tenant admin OR admin of ALL selected groups)
    const canEdit = computed(() => {
        // Tenant admins can always edit
        if (isTenantAdmin.value) return true;

        if (!selectedGroupIds.value || selectedGroupIds.value.length === 0) {
            return false; // No groups selected
        }

        // Must be admin of ALL selected groups
        return selectedGroupIds.value.every(id =>
            userAdminGroupIds.value.includes(id)
        );
    });

    // Check if user can view (tenant admin OR admin of at least ONE selected group)
    const canView = computed(() => {
        // Tenant admins can always view
        if (isTenantAdmin.value) return true;

        if (!selectedGroupIds.value || selectedGroupIds.value.length === 0) {
            return false;
        }

        // Must be admin of at least ONE group
        return selectedGroupIds.value.some(id =>
            userAdminGroupIds.value.includes(id)
        );
    });

    // Get groups user is NOT admin of (excluding tenant admins)
    const nonAdminGroups = computed(() => {
        // Tenant admins are admin of all groups
        if (isTenantAdmin.value) return [];

        if (!selectedGroupIds.value) return [];

        return selectedGroupIds.value.filter(id =>
            !userAdminGroupIds.value.includes(id)
        );
    });

    // Check if there are any non-admin groups (excluding tenant admins)
    const hasNonAdminGroups = computed(() => {
        // Tenant admins never have non-admin groups
        if (isTenantAdmin.value) return false;

        return nonAdminGroups.value.length > 0;
    });

    return {
        isTenantAdmin,
        userAdminGroupIds,
        canEdit,
        canView,
        nonAdminGroups,
        hasNonAdminGroups,
    };
}
